'use client';
import {
  REJECTION_REASON_TAGS,
  REJECTION_REASON_TAG_LABELS,
  RejectionReasonTag,
} from '@models/approval';
import { XIcon } from 'lucide-react';
import { useState } from 'react';

interface RejectReasonPickerProps {
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (args: {
    reasonTags: RejectionReasonTag[];
    reason?: string;
  }) => void;
}

export const RejectReasonPicker = ({
  isPending,
  onCancel,
  onConfirm,
}: RejectReasonPickerProps) => {
  const [selectedTags, setSelectedTags] = useState<RejectionReasonTag[]>([]);
  const [note, setNote] = useState('');

  const toggleTag = (tag: RejectionReasonTag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const requiresNote = selectedTags.includes('other');
  const canConfirm =
    selectedTags.length > 0 && (!requiresNote || note.trim().length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
        <h3 className="mb-1 text-base font-semibold text-zinc-800">
          Why reject this response?
        </h3>
        <p className="mb-4 text-sm text-zinc-500">
          Pick one or more tags so we can spot patterns. Add a note for anything
          that needs spelling out.
        </p>

        <div className="mb-4 flex flex-wrap gap-2">
          {REJECTION_REASON_TAGS.map((tag) => {
            const active = selectedTags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                disabled={isPending}
                className={`rounded-full border px-3 py-1 text-sm transition-colors disabled:opacity-50 ${
                  active
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50'
                }`}
              >
                {REJECTION_REASON_TAG_LABELS[tag]}
              </button>
            );
          })}
        </div>

        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={
            requiresNote
              ? 'Required — describe what went wrong'
              : 'Optional note (what should the AI have done?)'
          }
          className="mb-4 min-h-[6rem] w-full rounded-lg border border-zinc-300 bg-white p-3 text-sm leading-relaxed text-zinc-800 focus:border-red-400 focus:outline-none"
        />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isPending}
            className="rounded-lg bg-zinc-200 px-4 py-2 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-300 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onConfirm({
                reasonTags: selectedTags,
                reason: note.trim() ? note.trim() : undefined,
              })
            }
            disabled={isPending || !canConfirm}
            className="flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
          >
            {isPending ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <XIcon className="h-4 w-4" />
            )}
            Reject
          </button>
        </div>
      </div>
    </div>
  );
};
